import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useAuth } from '../lib/auth';
import { getDashboardStats, getSalesByProject, getSalesByVenditore, getExpiringContracts, getCommissionsByType, getRenewalAnalytics, getBusinessUnits, getProducts, getUsers, getMonthlySales, getMonthlySuccessFees, getDashboardRankings, getProductBreakdown, getPmBreakdown, getDashboardAlerts, getPivotData } from '../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { MultiSelect } from '../components/ui/multi-select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import DashboardFilters from '../components/DashboardFilters';
import PivotTab from '../components/PivotTab';
import SalesDashboardPage from './SalesDashboardPage';
import PMSummaryDashboardPage from './PMSummaryDashboardPage';
import FrontendAnalyticsPage from './FrontendAnalyticsPage';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  LineChart,
  Line,
  Area,
  AreaChart,
  ScatterChart,
  Scatter,
  ZAxis,
} from 'recharts';
import {
  TrendingUp,
  TrendingDown,
  Users,
  FileText,
  ShoppingCart,
  AlertTriangle,
  Euro,
  Building2,
  FolderKanban,
  CircleDollarSign,
  RefreshCw,
  X,
  Filter,
  BarChart3,
  HelpCircle,
  Percent,
  Target,
  Trophy,
  Crown,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  Package,
  ShieldAlert,
  Clock,
  Layers,
  UserCheck,
  CalendarClock,
  Maximize2,
} from 'lucide-react';
import PeriodPresets, { PERIOD_PRESETS as RENEWAL_DATE_PRESETS } from '../components/PeriodPresets';
import { format, subMonths, startOfMonth, endOfMonth, startOfQuarter, endOfQuarter, startOfYear, endOfYear, subQuarters, subYears } from 'date-fns';
import { it } from 'date-fns/locale';

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16'];
const TREND_COLORS = { primary: '#3B82F6', secondary: '#10B981', accent: '#F59E0B', danger: '#EF4444' };
const STATUS_COLORS = { renewed: '#10B981', withdrawn: '#8B5CF6', lost: '#EF4444' };

const MONTH_SHORT = ['Gen','Feb','Mar','Apr','Mag','Giu','Lug','Ago','Set','Ott','Nov','Dic'];
const MONTH_FULL = ['Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno','Luglio','Agosto','Settembre','Ottobre','Novembre','Dicembre'];

const formatCurrency = (value) => {
  if (value === null || value === undefined) return '€0,00';
  return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(value);
};

const formatCompact = (value) => {
  if (value === null || value === undefined) return '€0';
  if (Math.abs(value) >= 1000000) return `€${(value / 1000000).toFixed(1)}M`;
  if (Math.abs(value) >= 1000) return `€${(value / 1000).toFixed(1)}k`;
  return `€${value.toFixed(0)}`;
};

const formatMonthLabel = (m) => {
  if (!m) return '';
  if (m.includes('-Q')) { const [y, q] = m.split('-'); return `${q} '${y.slice(2)}`; }
  const [y, mo] = m.split('-');
  return `${MONTH_SHORT[parseInt(mo) - 1]} '${y.slice(2)}`;
};

const formatMonthFull = (m) => {
  if (!m) return '';
  if (m.includes('-Q')) { const [y, q] = m.split('-'); return `${q.slice(1)}° Trimestre ${y}`; }
  const [y, mo] = m.split('-');
  return `${MONTH_FULL[parseInt(mo) - 1]} ${y}`;
};

// Calculate previous period dates
const getPreviousPeriod = (startDate, endDate) => {
  if (!startDate || !endDate) return { start: null, end: null };
  const start = new Date(startDate);
  const end = new Date(endDate);
  const durationMs = end.getTime() - start.getTime();
  const prevEnd = new Date(start.getTime() - 86400000); // day before start
  const prevStart = new Date(prevEnd.getTime() - durationMs);
  return {
    start: prevStart.toISOString().split('T')[0],
    end: prevEnd.toISOString().split('T')[0],
  };
};

// ─── SPARKLINE COMPONENT ───
function Sparkline({ data, dataKey, positive, label }) {
  if (!data || data.length === 0) return null;
  const color = positive === null ? '#94a3b8' : positive ? '#10b981' : '#ef4444';
  const gradId = `sparkGrad-${dataKey}-${positive}`;
  const formatVal = (v) => {
    if (v === undefined || v === null) return '';
    if (typeof v === 'number') {
      if (dataKey?.includes('pct') || dataKey?.includes('percent')) return `${v.toFixed(1)}%`;
      if (v >= 1000) return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v);
      if (Number.isInteger(v)) return v.toString();
      return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 }).format(v);
    }
    return v;
  };
  return (
    <ResponsiveContainer width="100%" height={36}>
      <AreaChart data={data} margin={{ top: 2, right: 0, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.3} />
            <stop offset="100%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <Area type="monotone" dataKey={dataKey} stroke={color} strokeWidth={1.5} fill={`url(#${gradId})`} dot={false} />
        <RechartsTooltip
          contentStyle={{ background: '#1f2937', border: 'none', borderRadius: '6px', padding: '4px 8px', fontSize: '11px', color: '#f9fafb' }}
          labelStyle={{ display: 'none' }}
          formatter={(value) => [formatVal(value), label || dataKey]}
          cursor={{ stroke: color, strokeWidth: 1, strokeDasharray: '3 3' }}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}

// ─── DELTA BADGE COMPONENT ───
function DeltaBadge({ delta }) {
  if (delta === null || delta === undefined) return null;
  const isPositive = delta > 0;
  const isNeutral = delta === 0;
  const Icon = isPositive ? ArrowUpRight : isNeutral ? Minus : ArrowDownRight;
  const colorClass = isPositive ? 'text-emerald-700 bg-emerald-50 border-emerald-200' : isNeutral ? 'text-gray-500 bg-gray-50 border-gray-200' : 'text-red-700 bg-red-50 border-red-200';
  return (
    <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-xs font-semibold border ${colorClass}`} data-testid="delta-badge">
      <Icon className="w-3 h-3" />
      {isPositive ? '+' : ''}{delta.toFixed(1)}%
    </span>
  );
}

// ─── INLINE DELTA for table rows ───
function DeltaInline({ current, previous }) {
  if (previous === null || previous === undefined || previous === 0) return null;
  const delta = ((current - previous) / Math.abs(previous)) * 100;
  if (!isFinite(delta)) return null;
  const isPositive = delta > 0;
  const color = isPositive ? 'text-emerald-600' : delta < 0 ? 'text-red-500' : 'text-gray-400';
  return (
    <span className={`text-[10px] font-medium ${color} ml-1`} data-testid="delta-inline">
      {isPositive ? '+' : ''}{delta.toFixed(1)}%
    </span>
  );
}

// ─── KPI CARD COMPONENT ───
// ─── SORTABLE TABLE HEADER ───
function SortableHeader({ label, sortKey, currentSort, onSort, className = "text-right" }) {
  const isActive = currentSort.key === sortKey;
  const dir = isActive ? currentSort.dir : null;
  return (
    <TableHead
      className={`${className} font-semibold text-gray-600 cursor-pointer hover:text-gray-900 select-none transition-colors`}
      onClick={() => onSort(sortKey)}
      data-testid={`sort-${sortKey}`}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {isActive && (
          <span className="text-[10px]">{dir === 'asc' ? '\u25B2' : '\u25BC'}</span>
        )}
      </span>
    </TableHead>
  );
}

function useSortState(defaultKey = null, defaultDir = 'desc') {
  const [sort, setSort] = useState({ key: defaultKey, dir: defaultDir });
  const toggleSort = useCallback((key) => {
    setSort(prev => prev.key === key ? { key, dir: prev.dir === 'desc' ? 'asc' : 'desc' } : { key, dir: 'desc' });
  }, []);
  const sortData = useCallback((data, accessor) => {
    if (!sort.key || !data) return data;
    return [...data].sort((a, b) => {
      const va = accessor ? accessor(a, sort.key) : a[sort.key];
      const vb = accessor ? accessor(b, sort.key) : b[sort.key];
      return sort.dir === 'asc' ? (va > vb ? 1 : -1) : (va < vb ? 1 : -1);
    });
  }, [sort]);
  return { sort, toggleSort, sortData };
}

function KPICard({ title, value, formatType, delta, sparkData, sparkKey, sparkLabel, icon: Icon, iconBg }) {
  const displayValue = formatType === 'currency' ? formatCurrency(value)
    : formatType === 'compact' ? formatCompact(value)
    : formatType === 'percent' ? `${(value || 0).toFixed(1)}%`
    : (value || 0).toString();
  
  const isPositive = delta !== null && delta !== undefined ? delta > 0 : null;

  return (
    <Card className="relative overflow-hidden border border-gray-100 shadow-sm hover:shadow-md transition-shadow" data-testid={`kpi-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardContent className="pt-5 pb-3 px-5">
        <div className="flex justify-between items-start mb-1 gap-2">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider leading-tight">{title}</p>
          <div className={`p-1.5 rounded-lg flex-shrink-0 ${iconBg}`}>
            <Icon className="w-4 h-4 text-white" />
          </div>
        </div>
        <p className="text-2xl font-bold text-gray-900 tracking-tight">{displayValue}</p>
        <div className="flex items-center gap-2 mt-1.5">
          <DeltaBadge delta={delta} />
          {delta !== null && delta !== undefined && (
            <span className="text-[10px] text-gray-400">vs periodo prec.</span>
          )}
        </div>
        <div className="mt-2">
          <Sparkline data={sparkData} dataKey={sparkKey} positive={isPositive} label={sparkLabel} />
        </div>
      </CardContent>
    </Card>
  );
}

// ─── RANKING ROW COMPONENT ───
function RankingBadge({ position }) {
  if (position === 1) return <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-amber-100 text-amber-700 text-xs font-bold">1</span>;
  if (position === 2) return <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-100 text-gray-600 text-xs font-bold">2</span>;
  if (position === 3) return <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-orange-50 text-orange-600 text-xs font-bold">3</span>;
  return <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-50 text-gray-400 text-xs font-semibold">{position}</span>;
}


export default function DashboardPage() {
  const { user, hasRole } = useAuth();
  
  // Overview state
  const [stats, setStats] = useState(null);
  const [prevStats, setPrevStats] = useState(null);
  const [monthlySales, setMonthlySales] = useState([]);
  const [monthlySuccessFees, setMonthlySuccessFees] = useState({ months: [], types: [] });
  const [rankings, setRankings] = useState({ top_projects: [], top_clients: [], top_pms: [] });
  const [productBreakdown, setProductBreakdown] = useState([]);
  const [pmBreakdown, setPmBreakdown] = useState([]);
  const [dashAlerts, setDashAlerts] = useState([]);
  const [prevProductBreakdown, setPrevProductBreakdown] = useState([]);
  const [prevPmBreakdown, setPrevPmBreakdown] = useState([]);
  const [prevRankings, setPrevRankings] = useState(null);
  const [prevPivotData, setPrevPivotData] = useState(null);
  const [pivotData, setPivotData] = useState(null);
  const [pivotLoading, setPivotLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const prodSort = useSortState();
  const pmSort = useSortState();
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({});
  
  // Renewals Analytics State
  const [renewalData, setRenewalData] = useState(null);
  const [renewalFilters, setRenewalFilters] = useState({
    start_date: format(subMonths(new Date(), 12), 'yyyy-MM-dd'),
    end_date: format(new Date(), 'yyyy-MM-dd'),
    business_unit_ids: [],
    product_ids: [],
    pm_ids: [],
  });
  const [renewalDatePreset, setRenewalDatePreset] = useState('last_12_months');
  const [renewalLoading, setRenewalLoading] = useState(false);
  const [renewalTrendQuarterly, setRenewalTrendQuarterly] = useState(false);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [products, setProducts] = useState([]);
  const [pmUsers, setPmUsers] = useState([]);
  const [expiringContracts, setExpiringContracts] = useState([]);

  // Trend charts: expand + isolate legend
  const [expandedChart, setExpandedChart] = useState(null); // 'venduto' | 'sf' | null
  const [isolatedVendutoKey, setIsolatedVendutoKey] = useState(null);
  const [isolatedSFKey, setIsolatedSFKey] = useState(null);
  const [overviewQuarterly, setOverviewQuarterly] = useState(false);
  const overviewGranularityInit = useRef(true);

  useEffect(() => {
    loadData();
    loadRenewalFiltersData();
  }, [filters]);

  useEffect(() => {
    if (overviewGranularityInit.current) { overviewGranularityInit.current = false; return; }
    getMonthlySales(monthlySalesFilters()).then(res => setMonthlySales(res.data)).catch(err => console.error('Error loading monthly sales:', err));
  }, [overviewQuarterly]);

  const monthlySalesFilters = () => (overviewQuarterly ? { ...filters, granularity: 'quarter' } : filters);

  useEffect(() => {
    if (hasRole(['admin', 'project_manager', 'account'])) {
      loadRenewalAnalytics();
    }
  }, [renewalFilters]);

  useEffect(() => {
    if (activeTab === 'pivot') {
      loadPivotData();
    }
  }, [activeTab, filters]);

  const loadPivotData = async () => {
    setPivotLoading(true);
    try {
      const prevPeriod = getPreviousPeriod(filters.start_date, filters.end_date);
      const hasPrev = prevPeriod.start && prevPeriod.end;
      const promises = [getPivotData(filters)];
      if (hasPrev) {
        promises.push(getPivotData({ ...filters, start_date: prevPeriod.start, end_date: prevPeriod.end }));
      }
      const results = await Promise.all(promises);
      setPivotData(results[0].data);
      setPrevPivotData(hasPrev ? results[1]?.data : null);
    } catch (err) {
      console.error('Failed to load pivot data', err);
    }
    setPivotLoading(false);
  };

  const loadData = async () => {
    setLoading(true);
    try {
      // Fetch current period data + rankings
      const promises = [
        getDashboardStats(filters),
        getMonthlySales(monthlySalesFilters()),
        getMonthlySuccessFees(filters),
        getDashboardRankings(filters),
        getProductBreakdown(filters),
        getPmBreakdown(filters),
        getDashboardAlerts(filters),
      ];
      
      // Calculate and fetch previous period data for deltas
      const prevPeriod = getPreviousPeriod(filters.start_date, filters.end_date);
      const hasPrev = prevPeriod.start && prevPeriod.end;
      if (hasPrev) {
        const prevFilters = { ...filters, start_date: prevPeriod.start, end_date: prevPeriod.end };
        promises.push(
          getDashboardStats(prevFilters),
          getProductBreakdown(prevFilters),
          getPmBreakdown(prevFilters),
          getDashboardRankings(prevFilters),
        );
      }
      
      const results = await Promise.all(promises);
      setStats(results[0].data);
      setMonthlySales(results[1].data);
      setMonthlySuccessFees(results[2].data);
      setRankings(results[3].data);
      setProductBreakdown(results[4].data);
      setPmBreakdown(results[5].data);
      setDashAlerts(results[6].data);
      if (hasPrev) {
        setPrevStats(results[7]?.data || null);
        setPrevProductBreakdown(results[8]?.data || []);
        setPrevPmBreakdown(results[9]?.data || []);
        setPrevRankings(results[10]?.data || null);
      } else {
        setPrevStats(null);
        setPrevProductBreakdown([]);
        setPrevPmBreakdown([]);
        setPrevRankings(null);
      }
    } catch (err) {
      console.error('Error loading dashboard:', err);
    } finally {
      setLoading(false);
    }
    // Load expiring contracts (only for non-venditore roles)
    if (hasRole(['admin', 'project_manager', 'account'])) {
      try {
        const expiringFilters = {};
        if (filters.business_unit_id) expiringFilters.business_unit_id = filters.business_unit_id;
        if (filters.client_id) expiringFilters.client_id_filter = filters.client_id;
        if (filters.user_id) expiringFilters.pm_id = filters.user_id;
        const res = await getExpiringContracts(90, 'open', expiringFilters);
        setExpiringContracts(res.data || []);
      } catch (err) {
        console.error('Error loading expiring contracts:', err);
        setExpiringContracts([]);
      }
    }
  };

  const loadRenewalFiltersData = async () => {
    try {
      const [buRes, prodRes, usersRes] = await Promise.all([
        getBusinessUnits(),
        getProducts(),
        getUsers(),
      ]);
      setBusinessUnits(buRes.data);
      setProducts(prodRes.data);
      setPmUsers((usersRes.data || []).filter(u => {
        const roles = u.roles || [];
        return u.role === 'project_manager' || roles.includes('project_manager') || u.role === 'supervisor' || roles.includes('supervisor');
      }));
    } catch (err) {
      console.error('Error loading renewal filters:', err);
    }
  };

  const loadRenewalAnalytics = async () => {
    setRenewalLoading(true);
    try {
      const cleanFilters = {};
      if (renewalFilters.start_date) cleanFilters.start_date = renewalFilters.start_date;
      if (renewalFilters.end_date) cleanFilters.end_date = renewalFilters.end_date;
      if (renewalFilters.business_unit_ids.length) cleanFilters.business_unit_id = renewalFilters.business_unit_ids.join(',');
      if (renewalFilters.product_ids.length) cleanFilters.product_id = renewalFilters.product_ids.join(',');
      if (renewalFilters.pm_ids.length) cleanFilters.pm_id = renewalFilters.pm_ids.join(',');
      const res = await getRenewalAnalytics(cleanFilters);
      setRenewalData(res.data);
    } catch (err) {
      console.error('Error loading renewal analytics:', err);
    } finally {
      setRenewalLoading(false);
    }
  };

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleRenewalFilterChange = (key, value) => {
    setRenewalFilters(prev => {
      const next = { ...prev, [key]: value };
      if (key === 'business_unit_ids' && value.length) {
        const allowed = new Set(products.filter(p => value.includes(p.business_unit_id)).map(p => p.id));
        next.product_ids = prev.product_ids.filter(id => allowed.has(id));
      }
      return next;
    });
  };

  const handleRenewalDatePresetChange = (presetValue) => {
    setRenewalDatePreset(presetValue);
    if (presetValue === 'all') {
      setRenewalFilters(prev => ({ ...prev, start_date: '', end_date: '' }));
    } else if (presetValue !== 'custom') {
      const preset = RENEWAL_DATE_PRESETS.find(p => p.value === presetValue);
      if (preset) {
        const { start, end } = preset.getRange();
        setRenewalFilters(prev => ({ ...prev, start_date: start, end_date: end }));
      }
    }
  };

  const resetRenewalFilters = () => {
    setRenewalDatePreset('last_12_months');
    setRenewalFilters({
      start_date: format(subMonths(new Date(), 12), 'yyyy-MM-dd'),
      end_date: format(new Date(), 'yyyy-MM-dd'),
      business_unit_ids: [],
      product_ids: [],
      pm_ids: [],
    });
  };

  // ─── KPI CALCULATIONS ───
  const kpis = useMemo(() => {
    if (!stats) return null;
    const venduto = stats.total_sales || 0;
    const sfTotale = stats.total_commissions || 0;
    const progettiAttivi = stats.projects_count || 0;
    const nOperativi = stats.n_operativi || 0;
    const sfPercentVenduto = venduto > 0 ? (sfTotale / venduto) * 100 : 0;
    const sfMediaProgetto = progettiAttivi > 0 ? sfTotale / progettiAttivi : 0;
    const sfPerOperativo = nOperativi > 0 ? sfTotale / nOperativi : 0;

    // Delta calculations
    let deltaVenduto = null, deltaSfTotale = null, deltaSfPercent = null, deltaSfMedia = null, deltaProgetti = null, deltaSfPerOp = null;
    if (prevStats) {
      const pv = prevStats.total_sales || 0;
      const psf = prevStats.total_commissions || 0;
      const pp = prevStats.projects_count || 0;
      const pOps = prevStats.n_operativi || 0;
      const pSfPct = pv > 0 ? (psf / pv) * 100 : 0;
      const pSfMedia = pp > 0 ? psf / pp : 0;
      const pSfPerOp = pOps > 0 ? psf / pOps : 0;
      deltaVenduto = pv > 0 ? ((venduto - pv) / pv) * 100 : null;
      deltaSfTotale = psf > 0 ? ((sfTotale - psf) / psf) * 100 : null;
      deltaSfPercent = pSfPct > 0 ? sfPercentVenduto - pSfPct : null; // absolute diff for %
      deltaSfMedia = pSfMedia > 0 ? ((sfMediaProgetto - pSfMedia) / pSfMedia) * 100 : null;
      deltaProgetti = pp > 0 ? ((progettiAttivi - pp) / pp) * 100 : null;
      deltaSfPerOp = pSfPerOp > 0 ? ((sfPerOperativo - pSfPerOp) / pSfPerOp) * 100 : null;
    }

    return {
      venduto, sfTotale, sfPercentVenduto, sfMediaProgetto, progettiAttivi, nOperativi, sfPerOperativo,
      deltaVenduto, deltaSfTotale, deltaSfPercent, deltaSfMedia, deltaProgetti, deltaSfPerOp,
    };
  }, [stats, prevStats]);

  // Trend data for Success Fee chart - combine into line chart format
  const sfTrendData = useMemo(() => {
    if (!monthlySuccessFees.months?.length) return [];
    const types = monthlySuccessFees.types || [];
    let rows = monthlySuccessFees.months;
    if (overviewQuarterly) {
      const qMap = {};
      for (const m of rows) {
        const [y, mo] = m.month.split('-');
        const key = `${y}-Q${Math.ceil(parseInt(mo) / 3)}`;
        if (!qMap[key]) qMap[key] = { month: key };
        for (const t of types) qMap[key][t] = Math.round(((qMap[key][t] || 0) + (m[t] || 0)) * 100) / 100;
      }
      rows = Object.values(qMap).sort((a, b) => a.month.localeCompare(b.month));
    }
    return rows.map(m => {
      const total = types.reduce((sum, t) => sum + (m[t] || 0), 0);
      return { ...m, Totale: Math.round(total * 100) / 100 };
    });
  }, [monthlySuccessFees, overviewQuarterly]);

  // Scatter plot data from rankings (project quality)
  const scatterData = useMemo(() => {
    if (!rankings.top_projects?.length) return [];
    return rankings.top_projects
      .filter(p => p.venduto > 0 || p.success_fee > 0)
      .map(p => ({
        name: p.project_name,
        client: p.client_name,
        x: p.venduto,
        y: p.success_fee,
        z: Math.max(p.n_vendite, 1),
        sf_percent: p.sf_percent,
      }));
  }, [rankings]);

  // Previous period lookup maps for breakdown/ranking deltas
  const prevProdMap = useMemo(() => {
    const m = {};
    for (const p of prevProductBreakdown) m[p.product_id] = p;
    return m;
  }, [prevProductBreakdown]);

  const prevPmMap = useMemo(() => {
    const m = {};
    for (const p of prevPmBreakdown) m[p.pm_id] = p;
    return m;
  }, [prevPmBreakdown]);

  const prevProjectMap = useMemo(() => {
    const m = {};
    if (prevRankings?.top_projects) for (const p of prevRankings.top_projects) m[p.project_id] = p;
    return m;
  }, [prevRankings]);

  const prevPmRankMap = useMemo(() => {
    const m = {};
    if (prevRankings?.top_pms) for (const p of prevRankings.top_pms) m[p.pm_id] = p;
    return m;
  }, [prevRankings]);

  const prevVenditoriMap = useMemo(() => {
    const m = {};
    if (prevRankings?.top_venditori) for (const v of prevRankings.top_venditori) m[v.venditore_id] = v;
    return m;
  }, [prevRankings]);

  const hasPeriodFilter = !!(filters.start_date && filters.end_date);

  // Renewals derived data
  const pieData = renewalData ? [
    { name: 'Rinnovi', value: renewalData.kpis.renewals, fill: STATUS_COLORS.renewed },
    { name: 'Recessi', value: renewalData.kpis.withdrawals, fill: STATUS_COLORS.withdrawn },
    { name: 'Persi', value: renewalData.kpis.lost, fill: STATUS_COLORS.lost },
  ] : [];

  const valueBarData = renewalData ? [
    { name: 'Rinnovi', value: renewalData.kpis.renewals_value, fill: STATUS_COLORS.renewed },
    { name: 'Recessi', value: renewalData.kpis.withdrawals_value, fill: STATUS_COLORS.withdrawn },
    { name: 'Persi', value: renewalData.kpis.lost_value, fill: STATUS_COLORS.lost },
  ] : [];

  // Trend chart renderers (used both inline and inside expand dialog)
  const handleLegendClick = (setter, current) => (e) => {
    const key = e.dataKey;
    setter(current === key ? null : key);
  };
  const isVendutoHidden = (key) => isolatedVendutoKey !== null && isolatedVendutoKey !== key;
  const isSFHidden = (key) => isolatedSFKey !== null && isolatedSFKey !== key;

  const renderVendutoChart = (height) => (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={monthlySales} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
        <defs>
          <linearGradient id="gradVenduto" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.2} />
            <stop offset="100%" stopColor="#3B82F6" stopOpacity={0} />
          </linearGradient>
          <linearGradient id="gradComm" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#10B981" stopOpacity={0.2} />
            <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
        <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#94a3b8' }} tickFormatter={formatMonthLabel} />
        <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} tickFormatter={(v) => v >= 10000 ? `${(v/1000).toFixed(0)}k` : `€${v.toFixed(0)}`} />
        <RechartsTooltip
          formatter={(v, name) => [formatCurrency(v), name]}
          labelFormatter={formatMonthFull}
          contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '13px' }}
        />
        <Legend
          wrapperStyle={{ fontSize: '12px', cursor: 'pointer' }}
          onClick={handleLegendClick(setIsolatedVendutoKey, isolatedVendutoKey)}
        />
        <Area type="monotone" dataKey="total_sales" name="Venduto" stroke="#3B82F6" strokeWidth={2} fill="url(#gradVenduto)" dot={{ r: 3, fill: '#3B82F6' }} hide={isVendutoHidden('total_sales')} />
        <Area type="monotone" dataKey="total_commissions" name="Provvigioni" stroke="#10B981" strokeWidth={2} fill="url(#gradComm)" dot={{ r: 3, fill: '#10B981' }} hide={isVendutoHidden('total_commissions')} />
      </AreaChart>
    </ResponsiveContainer>
  );

  const renderSFChart = (height) => (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={sfTrendData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
        <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#94a3b8' }} tickFormatter={formatMonthLabel} />
        <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} tickFormatter={(v) => v >= 10000 ? `${(v/1000).toFixed(0)}k` : `€${v.toFixed(0)}`} />
        <RechartsTooltip
          content={({ active, payload, label }) => {
            if (!active || !payload || !payload.length) return null;
            const total = payload.reduce((sum, entry) => entry.dataKey !== 'Totale' ? sum + (entry.value || 0) : sum, 0);
            return (
              <div className="bg-white rounded-lg border border-gray-200 shadow-lg px-3 py-2.5 text-[13px]" data-testid="sf-tooltip">
                <p className="font-semibold text-gray-800 mb-1.5 border-b border-gray-100 pb-1">{formatMonthFull(label)}</p>
                {payload.map((entry) => {
                  const pct = entry.dataKey !== 'Totale' && total > 0 ? ((entry.value / total) * 100).toFixed(1) : null;
                  return (
                    <div key={entry.dataKey} className="flex items-center justify-between gap-4 py-0.5">
                      <span className="flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: entry.color }} />
                        <span style={{ color: entry.color }}>{entry.name}</span>
                      </span>
                      <span className="font-medium text-gray-900">
                        {formatCurrency(entry.value)}
                        {pct && <span className="ml-1.5 text-[11px] font-bold text-gray-500">({pct}%)</span>}
                      </span>
                    </div>
                  );
                })}
              </div>
            );
          }}
        />
        <Legend
          wrapperStyle={{ fontSize: '12px', cursor: 'pointer' }}
          onClick={handleLegendClick(setIsolatedSFKey, isolatedSFKey)}
        />
        {(monthlySuccessFees.types || []).map((typeName, idx) => (
          <Line
            key={typeName}
            type="monotone"
            dataKey={typeName}
            name={typeName}
            stroke={COLORS[idx % COLORS.length]}
            strokeWidth={2}
            dot={{ r: 3 }}
            hide={isSFHidden(typeName)}
          />
        ))}
        <Line type="monotone" dataKey="Totale" name="Totale" stroke="#1e293b" strokeWidth={2.5} strokeDasharray="6 3" dot={{ r: 3, fill: '#1e293b' }} hide={isSFHidden('Totale')} />
      </LineChart>
    </ResponsiveContainer>
  );

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-500">Benvenuto, {user?.name}</p>
      </div>

      <Tabs defaultValue="overview" onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Panoramica</TabsTrigger>
          <TabsTrigger value="pivot" className="flex items-center gap-2" data-testid="pivot-tab-trigger">
            <Layers className="w-4 h-4" />
            Analisi Pivot
          </TabsTrigger>
          {hasRole(['admin', 'hr', 'project_manager', 'account']) && (
            <TabsTrigger value="renewals" className="flex items-center gap-2">
              <RefreshCw className="w-4 h-4" />
              Analisi Rinnovi
            </TabsTrigger>
          )}
          {hasRole(['admin', 'hr', 'account']) && (
            <TabsTrigger value="vendite" className="flex items-center gap-2" data-testid="vendite-tab-trigger">
              <Euro className="w-4 h-4" />
              Vendite
            </TabsTrigger>
          )}
          {hasRole(['admin', 'hr', 'project_manager']) && (
            <TabsTrigger value="pm-summary" className="flex items-center gap-2" data-testid="pm-summary-tab-trigger">
              <UserCheck className="w-4 h-4" />
              Riepilogo PM
            </TabsTrigger>
          )}
          {hasRole(['admin', 'hr', 'project_manager', 'supervisor']) && (
            <TabsTrigger value="frontend-analytics" className="flex items-center gap-2" data-testid="frontend-analytics-tab-trigger">
              <BarChart3 className="w-4 h-4" />
              Analisi Front-End
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          {/* ══════ SECTION A: FILTERS ══════ */}
          <DashboardFilters filters={filters} onFiltersChange={handleFiltersChange} userRole={user?.role} />

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <>
              <div className="flex justify-end -mb-2">
                <label className="flex items-center gap-1.5 cursor-pointer select-none" data-testid="overview-quarterly-toggle" onClick={() => setOverviewQuarterly(!overviewQuarterly)}>
                  <div className={`relative w-9 h-5 rounded-full transition-colors ${overviewQuarterly ? 'bg-orange-500' : 'bg-gray-200'}`}>
                    <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${overviewQuarterly ? 'translate-x-4' : 'translate-x-0.5'}`} />
                  </div>
                  <span className="text-xs text-gray-500 font-medium">Vista trimestre</span>
                </label>
              </div>
              {/* ══════ SECTION B: KPI CORE ══════ */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="kpi-core-section">
                <KPICard
                  title="Venduto Totale"
                  value={kpis?.venduto}
                  formatType="currency"
                  delta={kpis?.deltaVenduto}
                  sparkData={monthlySales}
                  sparkKey="total_sales"
                  sparkLabel="Venduto"
                  icon={Euro}
                  iconBg="bg-blue-500"
                />
                <KPICard
                  title="Success Fee Totale"
                  value={kpis?.sfTotale}
                  formatType="currency"
                  delta={kpis?.deltaSfTotale}
                  sparkData={monthlySales}
                  sparkKey="total_commissions"
                  sparkLabel="SF"
                  icon={CircleDollarSign}
                  iconBg="bg-emerald-500"
                />
                <KPICard
                  title="SF% su Venduto"
                  value={kpis?.sfPercentVenduto}
                  formatType="percent"
                  delta={kpis?.deltaSfPercent}
                  sparkData={monthlySales.map(m => ({
                    ...m,
                    sf_pct: m.total_sales > 0 ? (m.total_commissions / m.total_sales) * 100 : 0
                  }))}
                  sparkKey="sf_pct"
                  sparkLabel="SF%"
                  icon={Percent}
                  iconBg="bg-violet-500"
                />
                <KPICard
                  title="SF/Progetto"
                  value={kpis?.sfMediaProgetto}
                  formatType="currency"
                  delta={kpis?.deltaSfMedia}
                  sparkData={monthlySales.map(m => ({
                    ...m,
                    sf_per_proj: m.n_projects > 0 ? m.total_commissions / m.n_projects : 0
                  }))}
                  sparkKey="sf_per_proj"
                  sparkLabel="SF/Prog."
                  icon={Target}
                  iconBg="bg-amber-500"
                />
                <KPICard
                  title="SF/Operativo"
                  value={kpis?.sfPerOperativo}
                  formatType="currency"
                  delta={kpis?.deltaSfPerOp}
                  sparkData={monthlySales.map(m => ({
                    ...m,
                    sf_per_op: m.n_operativi > 0 ? m.total_commissions / m.n_operativi : 0
                  }))}
                  sparkKey="sf_per_op"
                  sparkLabel="SF/Op."
                  icon={UserCheck}
                  iconBg="bg-teal-500"
                />
                <KPICard
                  title="Progetti Attivi"
                  value={kpis?.progettiAttivi}
                  formatType="number"
                  delta={kpis?.deltaProgetti}
                  sparkData={monthlySales.map(m => ({
                    ...m,
                    n_proj: m.n_projects || 0
                  }))}
                  sparkKey="n_proj"
                  sparkLabel="Progetti"
                  icon={FolderKanban}
                  iconBg="bg-slate-600"
                />
              </div>

              {/* ══════ SECTION C: TREND CHARTS ══════ */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="trend-section">
                {/* Trend Venduto */}
                <Card className="border border-gray-100 shadow-sm">
                  <CardHeader className="pb-2 flex flex-row items-start justify-between space-y-0">
                    <div>
                      <CardTitle className="text-base font-semibold text-gray-800">Trend Venduto</CardTitle>
                      <CardDescription>Andamento {overviewQuarterly ? 'trimestrale' : 'mensile'} del venduto e provvigioni</CardDescription>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-gray-400 hover:text-gray-700"
                      onClick={() => setExpandedChart('venduto')}
                      title="Espandi grafico"
                      data-testid="expand-venduto-btn"
                    >
                      <Maximize2 className="h-4 w-4" />
                    </Button>
                  </CardHeader>
                  <CardContent>
                    {monthlySales.length > 0 ? (
                      renderVendutoChart(280)
                    ) : (
                      <div className="h-[280px] flex items-center justify-center text-gray-400">Nessun dato disponibile</div>
                    )}
                    {isolatedVendutoKey && (
                      <div className="mt-2 text-center">
                        <button
                          type="button"
                          onClick={() => setIsolatedVendutoKey(null)}
                          className="text-xs text-blue-600 hover:text-blue-800 underline"
                          data-testid="reset-venduto-legend"
                        >
                          Mostra tutte le serie
                        </button>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Trend Success Fee per Tipologia */}
                <Card className="border border-gray-100 shadow-sm">
                  <CardHeader className="pb-2 flex flex-row items-start justify-between space-y-0">
                    <div>
                      <CardTitle className="text-base font-semibold text-gray-800">Trend Success Fee</CardTitle>
                      <CardDescription>Andamento {overviewQuarterly ? 'trimestrale' : 'mensile'} per tipologia</CardDescription>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-gray-400 hover:text-gray-700"
                      onClick={() => setExpandedChart('sf')}
                      title="Espandi grafico"
                      data-testid="expand-sf-btn"
                    >
                      <Maximize2 className="h-4 w-4" />
                    </Button>
                  </CardHeader>
                  <CardContent>
                    {sfTrendData.length > 0 ? (
                      renderSFChart(280)
                    ) : (
                      <div className="h-[280px] flex items-center justify-center text-gray-400">Nessun dato disponibile</div>
                    )}
                    {isolatedSFKey && (
                      <div className="mt-2 text-center">
                        <button
                          type="button"
                          onClick={() => setIsolatedSFKey(null)}
                          className="text-xs text-blue-600 hover:text-blue-800 underline"
                          data-testid="reset-sf-legend"
                        >
                          Mostra tutte le serie
                        </button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* ══════ SECTION D: RANKINGS ══════ */}
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-6" data-testid="ranking-section">
                {/* Top Progetti per SF */}
                <Card className="border border-gray-100 shadow-sm">
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-2">
                      <Trophy className="w-4 h-4 text-amber-500" />
                      <CardTitle className="text-base font-semibold text-gray-800">Top Progetti per SF</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className="px-0">
                    {rankings.top_projects?.length > 0 ? (
                      <div className="divide-y divide-gray-50">
                        {rankings.top_projects.map((p, idx) => {
                          const prev = prevProjectMap[p.project_id];
                          return (
                          <div key={p.project_id} className="flex items-center gap-3 px-5 py-2.5 hover:bg-gray-50/50 transition-colors">
                            <RankingBadge position={idx + 1} />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{p.project_name}</p>
                              <p className="text-xs text-gray-400">{p.client_name}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-semibold text-gray-900">
                                {formatCurrency(p.success_fee)}
                                <DeltaInline current={p.success_fee} previous={prev?.success_fee} />
                              </p>
                              <div className="flex items-center gap-1.5 justify-end">
                                <span className="text-xs text-gray-400">{formatCompact(p.venduto)}</span>
                                <Badge variant="outline" className={`text-[10px] px-1 py-0 ${p.sf_percent >= 20 ? 'text-emerald-600 border-emerald-200' : p.sf_percent >= 10 ? 'text-amber-600 border-amber-200' : 'text-gray-500 border-gray-200'}`}>
                                  {p.sf_percent.toFixed(1)}%
                                </Badge>
                              </div>
                            </div>
                          </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-center text-gray-400 text-sm py-8">Nessun dato</p>
                    )}
                  </CardContent>
                </Card>

                {/* Top Progetti per Venduto */}
                <Card className="border border-gray-100 shadow-sm">
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-2">
                      <Crown className="w-4 h-4 text-blue-500" />
                      <CardTitle className="text-base font-semibold text-gray-800">Top Progetti per Venduto</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className="px-0">
                    {rankings.top_projects?.length > 0 ? (
                      <div className="divide-y divide-gray-50">
                        {[...rankings.top_projects].sort((a, b) => b.venduto - a.venduto).map((p, idx) => {
                          const prev = prevProjectMap[p.project_id];
                          return (
                          <div key={p.project_id} className="flex items-center gap-3 px-5 py-2.5 hover:bg-gray-50/50 transition-colors">
                            <RankingBadge position={idx + 1} />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{p.project_name}</p>
                              <p className="text-xs text-gray-400">{p.client_name}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-semibold text-gray-900">
                                {formatCurrency(p.venduto)}
                                <DeltaInline current={p.venduto} previous={prev?.venduto} />
                              </p>
                              <span className="text-xs text-gray-400">{formatCompact(p.success_fee)} SF</span>
                            </div>
                          </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-center text-gray-400 text-sm py-8">Nessun dato</p>
                    )}
                  </CardContent>
                </Card>

                {/* Top PM per SF */}
                <Card className="border border-gray-100 shadow-sm">
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-violet-500" />
                      <CardTitle className="text-base font-semibold text-gray-800">Top PM per Success Fee</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className="px-0">
                    {rankings.top_pms?.length > 0 ? (
                      <div className="divide-y divide-gray-50">
                        {rankings.top_pms.map((pm, idx) => {
                          const prev = prevPmRankMap[pm.pm_id];
                          return (
                          <div key={pm.pm_id} className="flex items-center gap-3 px-5 py-2.5 hover:bg-gray-50/50 transition-colors">
                            <RankingBadge position={idx + 1} />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{pm.pm_name}</p>
                              <p className="text-xs text-gray-400">{pm.n_progetti} prog. / {pm.n_vendite} vendite</p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-semibold text-gray-900">
                                {formatCurrency(pm.success_fee)}
                                <DeltaInline current={pm.success_fee} previous={prev?.success_fee} />
                              </p>
                              <div className="flex items-center gap-1.5 justify-end">
                                <span className="text-xs text-gray-400">{formatCompact(pm.venduto)}</span>
                                <Badge variant="outline" className={`text-[10px] px-1 py-0 ${pm.sf_percent >= 20 ? 'text-emerald-600 border-emerald-200' : pm.sf_percent >= 10 ? 'text-amber-600 border-amber-200' : 'text-gray-500 border-gray-200'}`}>
                                  {pm.sf_percent.toFixed(1)}%
                                </Badge>
                              </div>
                            </div>
                          </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-center text-gray-400 text-sm py-8">Nessun dato</p>
                    )}
                  </CardContent>
                </Card>

                {/* Top Operativi per Success Fee */}
                <Card className="border border-gray-100 shadow-sm" data-testid="top-venditori-section">
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-2">
                      <UserCheck className="w-4 h-4 text-teal-500" />
                      <CardTitle className="text-base font-semibold text-gray-800">Top Operativi per SF</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className="px-0">
                    {rankings.top_venditori?.length > 0 ? (
                      <div className="divide-y divide-gray-50">
                        {rankings.top_venditori.map((v, idx) => {
                          const prev = prevVenditoriMap[v.venditore_id];
                          return (
                          <div key={v.venditore_id} className="flex items-center gap-3 px-5 py-2.5 hover:bg-gray-50/50 transition-colors">
                            <RankingBadge position={idx + 1} />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{v.venditore_name}</p>
                              <p className="text-xs text-gray-400">{v.n_vendite} vendite</p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-semibold text-emerald-700">
                                {formatCurrency(v.success_fee)}
                                <DeltaInline current={v.success_fee} previous={prev?.success_fee} />
                              </p>
                              <div className="flex items-center gap-1.5 justify-end">
                                <span className="text-xs text-gray-400">{formatCompact(v.venduto)} venduto</span>
                                <Badge variant="outline" className={`text-[10px] px-1 py-0 ${v.sf_percent >= 20 ? 'text-emerald-600 border-emerald-200' : v.sf_percent >= 10 ? 'text-amber-600 border-amber-200' : 'text-gray-500 border-gray-200'}`}>
                                  {v.sf_percent.toFixed(1)}%
                                </Badge>
                              </div>
                            </div>
                          </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-center text-gray-400 text-sm py-8">Nessun dato</p>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* ══════ SECTION E: PRODUCT BREAKDOWN TABLE ══════ */}
              <Card className="border border-gray-100 shadow-sm" data-testid="product-breakdown-section">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2">
                    <Package className="w-4 h-4 text-blue-500" />
                    <CardTitle className="text-base font-semibold text-gray-800">Breakdown per Prodotto</CardTitle>
                  </div>
                  <CardDescription>Performance di vendite e success fee per prodotto</CardDescription>
                </CardHeader>
                <CardContent>
                  {productBreakdown.length > 0 ? (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="border-gray-100">
                            <TableHead className="font-semibold text-gray-600">Prodotto</TableHead>
                            <SortableHeader label="Vendite" sortKey="vendite" currentSort={prodSort.sort} onSort={prodSort.toggleSort} />
                            <SortableHeader label="Success Fee" sortKey="success_fee" currentSort={prodSort.sort} onSort={prodSort.toggleSort} />
                            <SortableHeader label="Proj. Attivi" sortKey="n_progetti_attivi" currentSort={prodSort.sort} onSort={prodSort.toggleSort} />
                            <SortableHeader label="Media Vend." sortKey="media_vendite" currentSort={prodSort.sort} onSort={prodSort.toggleSort} />
                            <SortableHeader label="Media SF" sortKey="media_success_fee" currentSort={prodSort.sort} onSort={prodSort.toggleSort} />
                            <SortableHeader label="Media SF/Op." sortKey="media_sf_per_operativo" currentSort={prodSort.sort} onSort={prodSort.toggleSort} />
                            <SortableHeader label="SF %" sortKey="sf_percent" currentSort={prodSort.sort} onSort={prodSort.toggleSort} />
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {prodSort.sortData(productBreakdown).map((p) => {
                            const prev = prevProdMap[p.product_id];
                            return (
                            <TableRow key={p.product_id} className="hover:bg-gray-50/50 border-gray-50">
                              <TableCell className="font-medium text-gray-900">{p.product_name}</TableCell>
                              <TableCell className="text-right tabular-nums">
                                {formatCurrency(p.vendite)}<DeltaInline current={p.vendite} previous={prev?.vendite} />
                              </TableCell>
                              <TableCell className="text-right tabular-nums font-medium text-emerald-700">
                                {formatCurrency(p.success_fee)}<DeltaInline current={p.success_fee} previous={prev?.success_fee} />
                              </TableCell>
                              <TableCell className="text-right tabular-nums">{p.n_progetti_attivi}</TableCell>
                              <TableCell className="text-right tabular-nums text-gray-500">{formatCurrency(p.media_vendite)}</TableCell>
                              <TableCell className="text-right tabular-nums text-gray-500">{formatCurrency(p.media_success_fee)}</TableCell>
                              <TableCell className="text-right tabular-nums text-gray-500">{formatCurrency(p.media_sf_per_operativo)}</TableCell>
                              <TableCell className="text-right">
                                <Badge variant="outline" className={`text-xs px-1.5 py-0 ${p.sf_percent >= 15 ? 'text-emerald-600 border-emerald-200 bg-emerald-50' : p.sf_percent >= 8 ? 'text-amber-600 border-amber-200 bg-amber-50' : p.sf_percent > 0 ? 'text-red-600 border-red-200 bg-red-50' : 'text-gray-400 border-gray-200'}`}>
                                  {p.sf_percent.toFixed(1)}%
                                </Badge>
                              </TableCell>
                            </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <p className="text-center text-gray-400 text-sm py-8">Nessun dato disponibile</p>
                  )}
                </CardContent>
              </Card>

              {/* ══════ SECTION E2: PM BREAKDOWN TABLE ══════ */}
              <Card className="border border-gray-100 shadow-sm" data-testid="pm-breakdown-section">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-violet-500" />
                    <CardTitle className="text-base font-semibold text-gray-800">Breakdown per Project Manager</CardTitle>
                  </div>
                  <CardDescription>Performance di vendite e success fee per PM</CardDescription>
                </CardHeader>
                <CardContent>
                  {pmBreakdown.length > 0 ? (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="border-gray-100">
                            <TableHead className="font-semibold text-gray-600">Project Manager</TableHead>
                            <SortableHeader label="Vendite" sortKey="vendite" currentSort={pmSort.sort} onSort={pmSort.toggleSort} />
                            <SortableHeader label="Success Fee" sortKey="success_fee" currentSort={pmSort.sort} onSort={pmSort.toggleSort} />
                            <SortableHeader label="Proj. Attivi" sortKey="n_progetti_attivi" currentSort={pmSort.sort} onSort={pmSort.toggleSort} />
                            <SortableHeader label="Media Vend." sortKey="media_vendite" currentSort={pmSort.sort} onSort={pmSort.toggleSort} />
                            <SortableHeader label="Media SF" sortKey="media_success_fee" currentSort={pmSort.sort} onSort={pmSort.toggleSort} />
                            <SortableHeader label="Media SF/Op." sortKey="media_sf_per_operativo" currentSort={pmSort.sort} onSort={pmSort.toggleSort} />
                            <SortableHeader label="SF %" sortKey="sf_percent" currentSort={pmSort.sort} onSort={pmSort.toggleSort} />
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {pmSort.sortData(pmBreakdown).map((pm) => {
                            const prev = prevPmMap[pm.pm_id];
                            return (
                            <TableRow key={pm.pm_id} className="hover:bg-gray-50/50 border-gray-50">
                              <TableCell className="font-medium text-gray-900">{pm.pm_name}</TableCell>
                              <TableCell className="text-right tabular-nums">
                                {formatCurrency(pm.vendite)}<DeltaInline current={pm.vendite} previous={prev?.vendite} />
                              </TableCell>
                              <TableCell className="text-right tabular-nums font-medium text-emerald-700">
                                {formatCurrency(pm.success_fee)}<DeltaInline current={pm.success_fee} previous={prev?.success_fee} />
                              </TableCell>
                              <TableCell className="text-right tabular-nums">{pm.n_progetti_attivi}</TableCell>
                              <TableCell className="text-right tabular-nums text-gray-500">{formatCurrency(pm.media_vendite)}</TableCell>
                              <TableCell className="text-right tabular-nums text-gray-500">{formatCurrency(pm.media_success_fee)}</TableCell>
                              <TableCell className="text-right tabular-nums text-gray-500">{formatCurrency(pm.media_sf_per_operativo)}</TableCell>
                              <TableCell className="text-right">
                                <Badge variant="outline" className={`text-xs px-1.5 py-0 ${pm.sf_percent >= 15 ? 'text-emerald-600 border-emerald-200 bg-emerald-50' : pm.sf_percent >= 8 ? 'text-amber-600 border-amber-200 bg-amber-50' : pm.sf_percent > 0 ? 'text-red-600 border-red-200 bg-red-50' : 'text-gray-400 border-gray-200'}`}>
                                  {pm.sf_percent.toFixed(1)}%
                                </Badge>
                              </TableCell>
                            </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <p className="text-center text-gray-400 text-sm py-8">Nessun dato disponibile</p>
                  )}
                </CardContent>
              </Card>

              {/* ══════ SECTION F: SCATTER PLOT PROGETTI ══════ */}
              <Card className="border border-gray-100 shadow-sm" data-testid="scatter-plot-section">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base font-semibold text-gray-800">Mappa Progetti — Venduto vs Success Fee</CardTitle>
                  <CardDescription>Dimensione bolla = numero vendite. Identifica progetti profittevoli e outlier.</CardDescription>
                </CardHeader>
                <CardContent>
                  {scatterData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={350}>
                      <ScatterChart margin={{ top: 10, right: 30, left: 0, bottom: 10 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis
                          type="number"
                          dataKey="x"
                          name="Venduto"
                          tick={{ fontSize: 11, fill: '#94a3b8' }}
                          tickFormatter={(v) => v >= 1000 ? `€${(v/1000).toFixed(0)}k` : `€${v.toFixed(0)}`}
                          label={{ value: 'Venduto', position: 'insideBottomRight', offset: -5, style: { fontSize: 11, fill: '#64748b' } }}
                        />
                        <YAxis
                          type="number"
                          dataKey="y"
                          name="Success Fee"
                          tick={{ fontSize: 11, fill: '#94a3b8' }}
                          tickFormatter={(v) => v >= 1000 ? `€${(v/1000).toFixed(0)}k` : `€${v.toFixed(0)}`}
                          label={{ value: 'Success Fee', angle: -90, position: 'insideLeft', offset: 15, style: { fontSize: 11, fill: '#64748b' } }}
                        />
                        <ZAxis type="number" dataKey="z" range={[80, 600]} name="N. Vendite" />
                        <RechartsTooltip
                          cursor={{ strokeDasharray: '3 3' }}
                          contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '13px' }}
                          content={({ active, payload }) => {
                            if (!active || !payload?.length) return null;
                            const d = payload[0].payload;
                            return (
                              <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 space-y-1">
                                <p className="font-semibold text-gray-900 text-sm">{d.name}</p>
                                <p className="text-xs text-gray-500">{d.client}</p>
                                <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs pt-1 border-t border-gray-100">
                                  <span className="text-gray-500">Venduto:</span><span className="font-medium text-right">{formatCurrency(d.x)}</span>
                                  <span className="text-gray-500">Success Fee:</span><span className="font-medium text-right text-emerald-600">{formatCurrency(d.y)}</span>
                                  <span className="text-gray-500">SF%:</span><span className="font-medium text-right">{d.sf_percent.toFixed(1)}%</span>
                                  <span className="text-gray-500">N. Vendite:</span><span className="font-medium text-right">{d.z}</span>
                                </div>
                              </div>
                            );
                          }}
                        />
                        <Scatter
                          data={scatterData}
                          fill="#3B82F6"
                          fillOpacity={0.6}
                          stroke="#2563EB"
                          strokeWidth={1}
                        />
                      </ScatterChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[350px] flex items-center justify-center text-gray-400">Nessun dato disponibile</div>
                  )}
                </CardContent>
              </Card>

              {/* ══════ SECTION G: ALERTS ══════ */}
              {dashAlerts.length > 0 && (
                <Card className="border border-gray-100 shadow-sm" data-testid="alerts-section">
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-2">
                      <ShieldAlert className="w-4 h-4 text-amber-500" />
                      <CardTitle className="text-base font-semibold text-gray-800">Alert Dashboard</CardTitle>
                      <Badge variant="outline" className="text-xs">{dashAlerts.length}</Badge>
                    </div>
                    <CardDescription>Segnalazioni automatiche ordinate per priorita</CardDescription>
                  </CardHeader>
                  <CardContent className="px-0">
                    <div className="divide-y divide-gray-50">
                      {dashAlerts.map((alert, idx) => {
                        const severityConfig = {
                          high: { bg: 'bg-red-50', border: 'border-l-red-500', icon: AlertTriangle, iconColor: 'text-red-500', label: 'Alta', labelClass: 'bg-red-100 text-red-700' },
                          medium: { bg: 'bg-amber-50', border: 'border-l-amber-500', icon: Clock, iconColor: 'text-amber-500', label: 'Media', labelClass: 'bg-amber-100 text-amber-700' },
                          low: { bg: 'bg-blue-50', border: 'border-l-blue-400', icon: HelpCircle, iconColor: 'text-blue-400', label: 'Bassa', labelClass: 'bg-blue-100 text-blue-600' },
                        };
                        const cfg = severityConfig[alert.severity] || severityConfig.low;
                        const SevIcon = cfg.icon;
                        return (
                          <div key={idx} className={`flex items-center gap-3 px-5 py-3 border-l-4 ${cfg.border} ${cfg.bg} hover:brightness-[0.98] transition-all`}>
                            <SevIcon className={`w-4 h-4 shrink-0 ${cfg.iconColor}`} />
                            <p className="flex-1 text-sm text-gray-700">{alert.message}</p>
                            <Badge className={`text-[10px] px-1.5 py-0 ${cfg.labelClass} border-0`}>{cfg.label}</Badge>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* ══════ SECTION H: CONTRATTI IN SCADENZA ══════ */}
              {hasRole(['admin', 'project_manager', 'account']) && expiringContracts.length > 0 && (
                <Card className="border border-orange-200 shadow-sm" data-testid="expiring-contracts-section">
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-2">
                      <CalendarClock className="w-4 h-4 text-orange-500" />
                      <CardTitle className="text-base font-semibold text-gray-800">Contratti in Scadenza</CardTitle>
                      <Badge variant="outline" className="text-xs border-orange-300 text-orange-700 bg-orange-50">{expiringContracts.length}</Badge>
                    </div>
                    <CardDescription>Contratti con scadenza entro i prossimi 90 giorni</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="rounded-lg border border-gray-100 overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-gray-50/80 border-b border-gray-100">
                            <TableHead className="text-xs font-semibold text-gray-500 uppercase px-5 py-3">Cliente</TableHead>
                            <TableHead className="text-xs font-semibold text-gray-500 uppercase px-5 py-3">Prodotto</TableHead>
                            <TableHead className="text-xs font-semibold text-gray-500 uppercase px-5 py-3">Progetto</TableHead>
                            <TableHead className="text-xs font-semibold text-gray-500 uppercase px-5 py-3">PM</TableHead>
                            <TableHead className="text-xs font-semibold text-gray-500 uppercase px-5 py-3 text-right">Valore</TableHead>
                            <TableHead className="text-xs font-semibold text-gray-500 uppercase px-5 py-3 text-right">Scadenza</TableHead>
                            <TableHead className="text-xs font-semibold text-gray-500 uppercase px-5 py-3 text-right">Giorni</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {expiringContracts.map((c) => {
                            const urgencyClass = c.days_until_expiry <= 15
                              ? 'bg-red-50/60 border-l-4 border-l-red-400'
                              : c.days_until_expiry <= 30
                              ? 'bg-amber-50/40 border-l-4 border-l-amber-400'
                              : 'hover:bg-gray-50/50 border-l-4 border-l-transparent';
                            return (
                              <TableRow key={c.contract_product_id} className={`${urgencyClass} border-b border-gray-50`} data-testid="expiring-contract-row">
                                <TableCell className="font-medium text-gray-900 text-sm px-5 py-3">{c.client_name}</TableCell>
                                <TableCell className="text-sm text-gray-700 px-5 py-3">{c.product_name}</TableCell>
                                <TableCell className="text-sm text-gray-600 px-5 py-3">{c.project_names?.join(', ') || '-'}</TableCell>
                                <TableCell className="text-sm text-gray-600 px-5 py-3">{c.pm_names?.join(', ') || '-'}</TableCell>
                                <TableCell className="text-right tabular-nums text-sm font-medium px-5 py-3">{formatCurrency(c.value)}</TableCell>
                                <TableCell className="text-right text-sm text-gray-600 px-5 py-3">
                                  {c.end_date ? new Date(c.end_date).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
                                </TableCell>
                                <TableCell className="text-right px-5 py-3">
                                  <Badge variant="outline" className={`text-xs px-1.5 py-0 ${
                                    c.days_until_expiry <= 15 ? 'text-red-700 border-red-300 bg-red-100' :
                                    c.days_until_expiry <= 30 ? 'text-amber-700 border-amber-300 bg-amber-100' :
                                    'text-gray-600 border-gray-200'
                                  }`}>
                                    {c.days_until_expiry}g
                                  </Badge>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>

        {/* ══════ PIVOT ANALYSIS TAB ══════ */}
        <TabsContent value="pivot" className="space-y-6">
          <DashboardFilters filters={filters} onFiltersChange={handleFiltersChange} userRole={user?.role} />
          <PivotTab pivotData={pivotData} prevPivotData={prevPivotData} loading={pivotLoading} />
        </TabsContent>

        {/* ══════ RENEWALS ANALYTICS TAB (unchanged) ══════ */}
        {hasRole(['admin', 'hr', 'project_manager', 'account']) && (
          <TabsContent value="renewals" className="space-y-6">
            {/* Filters */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Filter className="w-4 h-4" />
                    Filtri
                  </CardTitle>
                  <Button variant="ghost" size="sm" onClick={resetRenewalFilters} data-testid="renewal-filters-reset">
                    <X className="w-4 h-4 mr-1" />
                    Reset
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <PeriodPresets
                  activePreset={renewalDatePreset || 'all'}
                  onPresetChange={handleRenewalDatePresetChange}
                  showCustomDates={renewalDatePreset === 'custom'}
                  startDate={renewalFilters.start_date}
                  endDate={renewalFilters.end_date}
                  onStartDateChange={(v) => handleRenewalFilterChange('start_date', v)}
                  onEndDateChange={(v) => handleRenewalFilterChange('end_date', v)}
                  testIdPrefix="renewal-period"
                />
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2" data-testid="renewal-filter-bu">
                    <Label>Business Unit</Label>
                    <MultiSelect
                      options={businessUnits.map(bu => ({ value: bu.id, label: bu.name }))}
                      selected={renewalFilters.business_unit_ids}
                      onChange={(v) => handleRenewalFilterChange('business_unit_ids', v)}
                      placeholder="Tutte"
                    />
                  </div>
                  <div className="space-y-2" data-testid="renewal-filter-product">
                    <Label>Prodotto</Label>
                    <MultiSelect
                      options={products
                        .filter(p => !renewalFilters.business_unit_ids.length || renewalFilters.business_unit_ids.includes(p.business_unit_id))
                        .map(p => ({ value: p.id, label: p.name }))}
                      selected={renewalFilters.product_ids}
                      onChange={(v) => handleRenewalFilterChange('product_ids', v)}
                      placeholder="Tutti"
                    />
                  </div>
                  <div className="space-y-2" data-testid="renewal-filter-pm">
                    <Label>Project Manager</Label>
                    <MultiSelect
                      options={pmUsers.map(pm => ({ value: pm.id, label: pm.name }))}
                      selected={renewalFilters.pm_ids}
                      onChange={(v) => handleRenewalFilterChange('pm_ids', v)}
                      placeholder="Tutti"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {renewalLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
              </div>
            ) : renewalData ? (
              <>
                {/* KPI Cards */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-gray-500">Esiti Totali</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">{renewalData.kpis.total_outcomes}</div>
                      <p className="text-xs text-gray-500">{formatCurrency(renewalData.kpis.total_value)}</p>
                    </CardContent>
                  </Card>
                  
                  <Card className="border-green-200 bg-green-50">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-green-700">Rinnovi</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold text-green-700">{renewalData.kpis.renewals}</div>
                      <p className="text-xs text-green-600">{formatCurrency(renewalData.kpis.renewals_value)}</p>
                    </CardContent>
                  </Card>
                  
                  <Card className="border-purple-200 bg-purple-50">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-purple-700">Recessi</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold text-purple-700">{renewalData.kpis.withdrawals}</div>
                      <p className="text-xs text-purple-600">{formatCurrency(renewalData.kpis.withdrawals_value)}</p>
                    </CardContent>
                  </Card>
                  
                  <Card className="border-red-200 bg-red-50">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-red-700">Persi</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold text-red-700">{renewalData.kpis.lost}</div>
                      <p className="text-xs text-red-600">{formatCurrency(renewalData.kpis.lost_value)}</p>
                    </CardContent>
                  </Card>
                  
                  <Card className="border-green-200">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-gray-500">% Rinnovi</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold text-green-600">{renewalData.kpis.renewal_rate}%</div>
                    </CardContent>
                  </Card>
                  
                  <Card className="border-purple-200">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-gray-500">% Recessi</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold text-purple-600">{renewalData.kpis.withdrawal_rate}%</div>
                    </CardContent>
                  </Card>
                </div>

                {/* Charts Row */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <Card>
                    <CardHeader>
                      <CardTitle>Distribuzione Esiti</CardTitle>
                      <CardDescription>Suddivisione per tipologia di esito</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {pieData.length > 0 ? (
                        <ResponsiveContainer width="100%" height={280}>
                          <PieChart>
                            <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90}
                              label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}>
                              {pieData.map((entry, index) => (
                                <Cell key={index} fill={entry.fill} />
                              ))}
                            </Pie>
                            <RechartsTooltip />
                            <Legend />
                          </PieChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="h-[280px] flex items-center justify-center text-gray-500">Nessun dato disponibile</div>
                      )}
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>Valore Economico per Esito</CardTitle>
                      <CardDescription>Confronto valori per tipologia</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {renewalData.kpis.total_value > 0 ? (
                        <ResponsiveContainer width="100%" height={280}>
                          <BarChart data={valueBarData} layout="vertical">
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis type="number" tickFormatter={(v) => `€${(v/1000).toFixed(0)}k`} />
                            <YAxis dataKey="name" type="category" width={80} />
                            <RechartsTooltip formatter={(v) => formatCurrency(v)} />
                            <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                              {valueBarData.map((entry, index) => (
                                <Cell key={index} fill={entry.fill} />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="h-[280px] flex items-center justify-center text-gray-500">Nessun dato disponibile</div>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Trend Chart */}
                {renewalData.trend.length > 0 && (
                  <Card>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle>Trend Conversione nel Tempo</CardTitle>
                          <CardDescription>Andamento % rinnovi e recessi per {renewalTrendQuarterly ? 'trimestre' : 'mese'}</CardDescription>
                        </div>
                        <label className="flex items-center gap-1.5 cursor-pointer select-none" data-testid="renewal-quarterly-toggle" onClick={() => setRenewalTrendQuarterly(!renewalTrendQuarterly)}>
                          <div className={`relative w-9 h-5 rounded-full transition-colors ${renewalTrendQuarterly ? 'bg-orange-500' : 'bg-gray-200'}`}>
                            <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${renewalTrendQuarterly ? 'translate-x-4' : 'translate-x-0.5'}`} />
                          </div>
                          <span className="text-xs text-gray-500 font-medium">Per trimestre</span>
                        </label>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={(() => {
                          if (!renewalTrendQuarterly) return renewalData.trend;
                          const qMap = {};
                          for (const m of renewalData.trend) {
                            const [y, mo] = m.period.split('-');
                            const q = `${y}-Q${Math.ceil(parseInt(mo) / 3)}`;
                            if (!qMap[q]) qMap[q] = { period: q, total_outcomes: 0, renewals: 0, withdrawals: 0, lost: 0 };
                            qMap[q].total_outcomes += m.total_outcomes;
                            qMap[q].renewals += m.renewals;
                            qMap[q].withdrawals += m.withdrawals;
                            qMap[q].lost += m.lost;
                          }
                          return Object.values(qMap).map(s => ({
                            ...s,
                            renewal_rate: s.total_outcomes > 0 ? Math.round(s.renewals / s.total_outcomes * 1000) / 10 : 0,
                            withdrawal_rate: s.total_outcomes > 0 ? Math.round(s.withdrawals / s.total_outcomes * 1000) / 10 : 0,
                          }));
                        })()}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="period" tick={{ fontSize: 12 }} tickFormatter={formatMonthLabel} />
                          <YAxis tickFormatter={(v) => `${v}%`} domain={[0, 100]} />
                          <RechartsTooltip labelFormatter={formatMonthFull} formatter={(value, name) => [`${value}%`, name === 'renewal_rate' ? '% Rinnovi' : '% Recessi']} />
                          <Legend formatter={(value) => value === 'renewal_rate' ? '% Rinnovi' : '% Recessi'} />
                          <Line type="monotone" dataKey="renewal_rate" stroke={STATUS_COLORS.renewed} strokeWidth={2} dot={{ r: 4 }} />
                          <Line type="monotone" dataKey="withdrawal_rate" stroke={STATUS_COLORS.withdrawn} strokeWidth={2} dot={{ r: 4 }} />
                        </LineChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                )}

                {/* Breakdown Tables */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Building2 className="w-4 h-4" />
                        Per Business Unit
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {renewalData.by_business_unit.length > 0 ? (
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Business Unit</TableHead>
                              <TableHead className="text-right">Esiti</TableHead>
                              <TableHead className="text-right">Rinn.</TableHead>
                              <TableHead className="text-right">% Rinn.</TableHead>
                              <TableHead className="text-right">Valore</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {renewalData.by_business_unit.map((row) => (
                              <TableRow key={row.id}>
                                <TableCell className="font-medium">{row.name}</TableCell>
                                <TableCell className="text-right">{row.total_outcomes}</TableCell>
                                <TableCell className="text-right text-green-600">{row.renewals}</TableCell>
                                <TableCell className="text-right">
                                  <Badge variant="outline" className={row.renewal_rate >= 50 ? 'text-green-600 border-green-300' : 'text-orange-600 border-orange-300'}>
                                    {row.renewal_rate}%
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-right">{formatCurrency(row.renewals_value)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      ) : (
                        <p className="text-gray-500 text-center py-4">Nessun dato</p>
                      )}
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <BarChart3 className="w-4 h-4" />
                        Per Prodotto
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {renewalData.by_product.length > 0 ? (
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Prodotto</TableHead>
                              <TableHead className="text-right">Esiti</TableHead>
                              <TableHead className="text-right">Rinn.</TableHead>
                              <TableHead className="text-right">% Rinn.</TableHead>
                              <TableHead className="text-right">Valore</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {renewalData.by_product.map((row) => (
                              <TableRow key={row.id}>
                                <TableCell className="font-medium">{row.name}</TableCell>
                                <TableCell className="text-right">{row.total_outcomes}</TableCell>
                                <TableCell className="text-right text-green-600">{row.renewals}</TableCell>
                                <TableCell className="text-right">
                                  <Badge variant="outline" className={row.renewal_rate >= 50 ? 'text-green-600 border-green-300' : 'text-orange-600 border-orange-300'}>
                                    {row.renewal_rate}%
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-right">{formatCurrency(row.renewals_value)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      ) : (
                        <p className="text-gray-500 text-center py-4">Nessun dato</p>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* By PM */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      Per Project Manager
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {renewalData.by_pm.length > 0 ? (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Project Manager</TableHead>
                            <TableHead className="text-right">Esiti</TableHead>
                            <TableHead className="text-right">Rinnovi</TableHead>
                            <TableHead className="text-right">Recessi</TableHead>
                            <TableHead className="text-right">Persi</TableHead>
                            <TableHead className="text-right">% Rinnovi</TableHead>
                            <TableHead className="text-right">% Recessi</TableHead>
                            <TableHead className="text-right">Valore Rinnovi</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {renewalData.by_pm.map((row) => (
                            <TableRow key={row.id}>
                              <TableCell className="font-medium">{row.name}</TableCell>
                              <TableCell className="text-right">{row.total_outcomes}</TableCell>
                              <TableCell className="text-right text-green-600">{row.renewals}</TableCell>
                              <TableCell className="text-right text-purple-600">{row.withdrawals}</TableCell>
                              <TableCell className="text-right text-red-600">{row.lost}</TableCell>
                              <TableCell className="text-right">
                                <Badge variant="outline" className={row.renewal_rate >= 50 ? 'text-green-600 border-green-300' : 'text-orange-600 border-orange-300'}>
                                  {row.renewal_rate}%
                                </Badge>
                              </TableCell>
                              <TableCell className="text-right">
                                <Badge variant="outline" className="text-purple-600 border-purple-300">
                                  {row.withdrawal_rate}%
                                </Badge>
                              </TableCell>
                              <TableCell className="text-right font-medium">{formatCurrency(row.renewals_value)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    ) : (
                      <p className="text-gray-500 text-center py-4">Nessun dato disponibile</p>
                    )}
                  </CardContent>
                </Card>
              </>
            ) : (
              <div className="text-center py-12 text-gray-500">
                Seleziona un periodo per visualizzare i dati
              </div>
            )}
          </TabsContent>
        )}

        {/* ══════ VENDITE DASHBOARD TAB ══════ */}
        {hasRole(['admin', 'hr', 'account']) && (
          <TabsContent value="vendite" className="space-y-6">
            <SalesDashboardPage />
          </TabsContent>
        )}

        {/* ══════ PM SUMMARY DASHBOARD TAB ══════ */}
        {hasRole(['admin', 'hr', 'project_manager']) && (
          <TabsContent value="pm-summary" className="space-y-6">
            <PMSummaryDashboardPage />
          </TabsContent>
        )}

        {/* ══════ FRONTEND ANALYTICS DASHBOARD TAB ══════ */}
        {hasRole(['admin', 'hr', 'project_manager', 'supervisor']) && (
          <TabsContent value="frontend-analytics" className="space-y-6">
            <FrontendAnalyticsPage />
          </TabsContent>
        )}
      </Tabs>

      {/* ══════ EXPAND CHART DIALOG ══════ */}
      <Dialog open={expandedChart !== null} onOpenChange={(open) => !open && setExpandedChart(null)}>
        <DialogContent className="max-w-6xl w-[95vw]" data-testid="expanded-chart-dialog">
          <DialogHeader>
            <DialogTitle>
              {expandedChart === 'venduto' ? 'Trend Venduto' : 'Trend Success Fee'}
            </DialogTitle>
            <DialogDescription>
              {expandedChart === 'venduto'
                ? `Andamento ${overviewQuarterly ? 'trimestrale' : 'mensile'} del venduto e provvigioni`
                : `Andamento ${overviewQuarterly ? 'trimestrale' : 'mensile'} per tipologia`}
              {' '}· Clicca su una voce della legenda per isolarla
            </DialogDescription>
          </DialogHeader>
          <div className="mt-2">
            {expandedChart === 'venduto' && (monthlySales.length > 0
              ? renderVendutoChart(560)
              : <div className="h-[560px] flex items-center justify-center text-gray-400">Nessun dato disponibile</div>
            )}
            {expandedChart === 'sf' && (sfTrendData.length > 0
              ? renderSFChart(560)
              : <div className="h-[560px] flex items-center justify-center text-gray-400">Nessun dato disponibile</div>
            )}
            {((expandedChart === 'venduto' && isolatedVendutoKey) || (expandedChart === 'sf' && isolatedSFKey)) && (
              <div className="mt-3 text-center">
                <button
                  type="button"
                  onClick={() => (expandedChart === 'venduto' ? setIsolatedVendutoKey(null) : setIsolatedSFKey(null))}
                  className="text-sm text-blue-600 hover:text-blue-800 underline"
                  data-testid="reset-expanded-legend"
                >
                  Mostra tutte le serie
                </button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
